import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, MessageSquare, Zap, ShoppingCart, Clock, RefreshCw, Sparkles, Mic } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import PeriodFilter, { type Period } from '../components/PeriodFilter'
import CsvControls from '../components/CsvControls'

interface Overview {
  total_queries: number
  cache_hits: number
  // Concierge queries that COULD have hit cache (excludes advisor + rec_batch).
  // Use as the denominator for cache hit rate so the metric stays meaningful.
  cacheable_queries: number
  llm_calls: number
  total_cost_micros: number
  converted: number
  avg_latency_ms: number
  // Voice adoption — drives the voice-out decision (ship TTS only if
  // voice-in gets meaningful use). Rate is voice_queries / concierge_queries.
  voice_queries: number
  concierge_queries: number
}

interface LogEntry {
  id: string
  user_id: string
  user_name: string | null
  user_phone: string | null
  query: string
  locale: string
  response_type: string
  cache_tier: string | null
  llm_input_tokens: number | null
  llm_output_tokens: number | null
  llm_cache_read_tokens: number | null
  llm_cost_micros: number | null
  response_time_ms: number
  converted_to_cart: number
  matched_query: string | null
  matched_similarity: number | null
  source: string | null
  created_at: string
}

interface TopQuery {
  normalized: string
  times_asked: number
  cache_hits: number
  converted: number
}

interface CostByMode {
  response_type: string
  llm_calls: number
  cache_hits: number
  total_cost_micros: number
  avg_cost_micros: number
  avg_latency_ms: number
}

const RESPONSE_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  autocomplete:    { label: 'Autocomplete',   color: 'bg-gray-500/15 text-gray-400' },
  question:        { label: 'Question',       color: 'bg-blue-500/15 text-blue-400' },
  recommendation:  { label: 'Recommendation', color: 'bg-emerald-500/15 text-emerald-400' },
  advisor:         { label: 'Advisor',        color: 'bg-cyan-500/15 text-cyan-400' },
  mixologist:      { label: 'Mixologist',     color: 'bg-fuchsia-500/15 text-fuchsia-400' },
  transcribe:      { label: 'Voice',          color: 'bg-pink-500/15 text-pink-400' },
  out_of_scope:    { label: 'Out of scope',   color: 'bg-amber-500/15 text-amber-400' },
  rate_limited:    { label: 'Rate-limited',   color: 'bg-orange-500/15 text-orange-400' },
  error:           { label: 'Error',          color: 'bg-red-500/15 text-red-400' },
  rec_batch:       { label: 'Rec batch',      color: 'bg-purple-500/15 text-purple-400' },
}

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  concierge: { label: 'Concierge',  color: 'bg-[#C41E3A]/15 text-[#C41E3A]' },
  rec_batch: { label: 'Rec engine', color: 'bg-purple-500/15 text-purple-400' },
}

const CACHE_TIER_LABELS: Record<string, { label: string; color: string }> = {
  none:     { label: 'LLM',      color: 'text-[#C41E3A]' },
  exact:    { label: 'Cache (exact)',  color: 'text-emerald-400' },
  semantic: { label: 'Cache (semantic)', color: 'text-emerald-400' },
}

function formatCost(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(4)}`
}

export default function AiLogs() {
  const { can } = useAuth()
  const [period, setPeriod] = useState<Period>('7d')
  const qc = useQueryClient()
  const params = `period=${period}`

  const overview = useQuery<Overview>({
    queryKey: ['ai-overview', period],
    queryFn: () => api.get(`/ai-logs/overview?${params}`).then(r => r.data),
    refetchInterval: 30_000,
  })

  const logs = useQuery<{ logs: LogEntry[] }>({
    queryKey: ['ai-logs', period],
    queryFn: () => api.get(`/ai-logs?${params}&limit=100`).then(r => r.data),
    refetchInterval: 30_000,
  })

  const topQueries = useQuery<{ queries: TopQuery[] }>({
    queryKey: ['ai-top-queries', period],
    queryFn: () => api.get(`/ai-logs/top-queries?${params}`).then(r => r.data),
  })

  const costByMode = useQuery<{ modes: CostByMode[] }>({
    queryKey: ['ai-cost-by-mode', period],
    queryFn: () => api.get(`/ai-logs/cost-by-mode?${params}`).then(r => r.data),
  })

  // Chunked backfill loop — calls the endpoint repeatedly until done. Lets us
  // stay under Workers AI rate limits and surface progress to the admin.
  const [backfillProgress, setBackfillProgress] = useState<{
    total: number; processed: number; succeeded: number; failed: number
    errors: Array<{ product_id: string; error: string }>; running: boolean
    done: boolean
  } | null>(null)

  async function runBackfill() {
    setBackfillProgress({ total: 0, processed: 0, succeeded: 0, failed: 0, errors: [], running: true, done: false })
    let offset = 0
    const limit = 25
    let total = 0
    let totalSucceeded = 0
    let totalFailed = 0
    const allErrors: Array<{ product_id: string; error: string }> = []
    let done = false
    while (!done) {
      try {
        const r = await api.post(`/ai-logs/backfill-product-embeddings?offset=${offset}&limit=${limit}`)
        const data = r.data as {
          total: number; processed: number; done: boolean
          chunk: { offset: number; limit: number; succeeded: number; failed: number }
          errors: Array<{ product_id: string; error: string }>
        }
        total = data.total
        totalSucceeded += data.chunk.succeeded
        totalFailed    += data.chunk.failed
        for (const e of data.errors) {
          if (allErrors.length < 10) allErrors.push(e)
        }
        offset = data.processed
        done = data.done
        setBackfillProgress({
          total, processed: offset, succeeded: totalSucceeded, failed: totalFailed,
          errors: allErrors, running: !done, done,
        })
        if (totalFailed > 50 && totalSucceeded === 0) {
          // Hard stop — every product is failing, no point hammering further.
          break
        }
      } catch (e: any) {
        const msg = e?.response?.data?.message ?? e?.message ?? 'Unknown error'
        setBackfillProgress(p => p ? { ...p, running: false, errors: [...p.errors, { product_id: '(http)', error: msg }] } : null)
        return
      }
    }
    qc.invalidateQueries({ queryKey: ['ai-overview'] })
  }

  // Denominator is cacheable_queries (concierge non-advisor), not total —
  // otherwise advisor + rec-batch rows dilute the metric to meaninglessness.
  const cacheHitRate = overview.data && overview.data.cacheable_queries > 0
    ? overview.data.cache_hits / overview.data.cacheable_queries
    : 0
  const conversionRate = overview.data && overview.data.llm_calls > 0
    ? overview.data.converted / (overview.data.llm_calls)
    : 0
  // Voice adoption — % of concierge queries that used the mic instead
  // of typing. Denominator is concierge_queries (not total_queries) so
  // rec-batch runs don't dilute the number. Read this before deciding
  // whether to invest in voice-out (TTS) per the original backlog spec.
  const voiceAdoptionRate = overview.data && overview.data.concierge_queries > 0
    ? overview.data.voice_queries / overview.data.concierge_queries
    : 0

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bot size={24} className="text-[#C41E3A]" />
          <h1 className="text-2xl font-bold text-white">DrinX AI</h1>
        </div>
        <div className="flex items-center gap-3">
          <CsvControls
            basePath="/ai-logs"
            exportFilename={`ai-logs-export-${new Date().toISOString().slice(0, 10)}.csv`}
            exportOnly
            exportQuery={`?period=${period}`}
          />
          <PeriodFilter value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi icon={<MessageSquare size={16} />} label="Queries" value={`${overview.data?.total_queries ?? 0}`} />
        <Kpi icon={<Zap size={16} />} label="Cache hit rate" value={`${(cacheHitRate * 100).toFixed(1)}%`} sublabel={`${overview.data?.cache_hits ?? 0} of ${overview.data?.cacheable_queries ?? 0} concierge queries`} />
        <Kpi icon={<ShoppingCart size={16} />} label="Conversion" value={`${(conversionRate * 100).toFixed(1)}%`} sublabel={`${overview.data?.converted ?? 0} added to cart`} />
        <Kpi icon={<Mic size={16} />} label="Voice adoption" value={`${(voiceAdoptionRate * 100).toFixed(1)}%`} sublabel={`${overview.data?.voice_queries ?? 0} of ${overview.data?.concierge_queries ?? 0} concierge queries`} />
        <Kpi icon={<Clock size={16} />} label="Avg latency" value={`${Math.round(overview.data?.avg_latency_ms ?? 0)}ms`} />
        <Kpi icon={<Sparkles size={16} />} label="LLM spend" value={formatCost(overview.data?.total_cost_micros ?? 0)} sublabel="in this period" />
      </div>

      {/* Backfill card — super-admin only */}
      {can(['super_admin']) && (
        <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-white mb-1">Product embedding backfill</h3>
              <p className="text-xs text-gray-400">
                Embeds all active products into Vectorize so AI search can find them. Runs in chunks of 25.
                Auto-refresh runs after every product edit — manual backfill is rarely needed.
              </p>
              {backfillProgress && (
                <div className="mt-3">
                  {backfillProgress.total > 0 && (
                    <div className="mb-2">
                      <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                        <span>{backfillProgress.processed} / {backfillProgress.total}</span>
                        <span>
                          <span className="text-emerald-400">{backfillProgress.succeeded} ok</span>
                          {backfillProgress.failed > 0 && <span className="text-amber-400"> · {backfillProgress.failed} failed</span>}
                        </span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-1.5">
                        <div
                          className="bg-[#C41E3A] h-1.5 rounded-full transition-all"
                          style={{ width: `${Math.round((backfillProgress.processed / backfillProgress.total) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {backfillProgress.done && (
                    <p className="text-xs text-emerald-400 mt-1">
                      ✓ Complete — {backfillProgress.succeeded} / {backfillProgress.total} embedded
                    </p>
                  )}
                  {backfillProgress.errors.length > 0 && (
                    <div className="mt-2 bg-red-500/10 border border-red-500/20 rounded p-2">
                      <p className="text-[11px] text-red-300 font-semibold mb-1">
                        Failures ({backfillProgress.failed}) — sample errors:
                      </p>
                      <ul className="text-[10px] text-red-300/80 space-y-0.5 font-mono">
                        {backfillProgress.errors.slice(0, 5).map((e, i) => (
                          <li key={i} className="truncate" title={e.error}>
                            {e.product_id.slice(0, 8)}… → {e.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={runBackfill}
              disabled={backfillProgress?.running === true}
              className="px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              {backfillProgress?.running ? (
                <><RefreshCw size={14} className="animate-spin" /> Embedding…</>
              ) : (
                <><RefreshCw size={14} /> Run backfill</>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent queries — main table */}
        <div className="lg:col-span-2 bg-white/[0.02] border border-[#2e2e2e] rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-[#2e2e2e]">
            <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Recent queries</h3>
          </div>
          {logs.isLoading && <p className="text-gray-400 text-sm px-4 py-6">Loading…</p>}
          {!logs.isLoading && (logs.data?.logs.length ?? 0) === 0 && (
            <p className="text-gray-500 text-sm text-center py-12">No AI activity yet.</p>
          )}
          {logs.data && logs.data.logs.length > 0 && (
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] sticky top-0">
                  <tr className="text-gray-500 text-[10px] uppercase tracking-wide">
                    <th className="text-left px-4 py-2 font-semibold">Query</th>
                    <th className="text-left px-2 py-2 font-semibold">Source</th>
                    <th className="text-left px-2 py-2 font-semibold">Type</th>
                    <th className="text-right px-2 py-2 font-semibold">Cost</th>
                    <th className="text-right px-2 py-2 font-semibold">Latency</th>
                    <th className="text-left px-4 py-2 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.data.logs.map(l => {
                    const sourceKey = l.source ?? 'concierge'
                    const sourceTag = SOURCE_LABELS[sourceKey]
                    const isBatch = sourceKey === 'rec_batch'
                    return (
                      <tr key={l.id} className="border-t border-[#2e2e2e] hover:bg-white/[0.01]">
                        <td className="px-4 py-2.5">
                          <p className="text-white truncate max-w-[300px]" title={l.query}>{l.query}</p>
                          <p className="text-[10px] text-gray-500 flex items-center gap-2 mt-0.5">
                            {!isBatch && (
                              <span>{l.user_name ?? l.user_phone ?? (l.user_id?.slice(0, 8) ?? '—')}</span>
                            )}
                            {isBatch && l.llm_input_tokens != null && (
                              <span className="text-gray-400">
                                {l.llm_input_tokens.toLocaleString()}in · {(l.llm_output_tokens ?? 0).toLocaleString()}out
                                {l.llm_cache_read_tokens ? ` · ${l.llm_cache_read_tokens.toLocaleString()} cached` : ''}
                              </span>
                            )}
                            {l.cache_tier && (
                              <span className={CACHE_TIER_LABELS[l.cache_tier]?.color ?? 'text-gray-500'}>
                                · {CACHE_TIER_LABELS[l.cache_tier]?.label ?? l.cache_tier}
                              </span>
                            )}
                            {l.matched_query && (
                              <span className="text-gray-600 italic">· matched "{l.matched_query.slice(0, 30)}{l.matched_query.length > 30 ? '…' : ''}" @ {(l.matched_similarity ?? 0).toFixed(2)}</span>
                            )}
                            {l.converted_to_cart === 1 && (
                              <span className="text-emerald-400">· → cart</span>
                            )}
                          </p>
                        </td>
                        <td className="px-2 py-2.5">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${sourceTag?.color ?? 'bg-gray-500/15 text-gray-400'}`}>
                            {sourceTag?.label ?? sourceKey}
                          </span>
                        </td>
                        <td className="px-2 py-2.5">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${RESPONSE_TYPE_LABELS[l.response_type]?.color ?? 'bg-gray-500/15 text-gray-400'}`}>
                            {RESPONSE_TYPE_LABELS[l.response_type]?.label ?? l.response_type}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-right text-gray-300 tabular-nums whitespace-nowrap">
                          {l.llm_cost_micros ? formatCost(l.llm_cost_micros) : '—'}
                        </td>
                        <td className="px-2 py-2.5 text-right text-gray-400 tabular-nums whitespace-nowrap">
                          {l.response_time_ms}ms
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(l.created_at)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Side panels: cost-by-mode + top queries */}
        <div className="space-y-4">
          {/* Cost per mode — average $ per call grouped by response type.
              Cache hits and non-LLM rows are excluded server-side so the
              average reflects actual LLM spend per mode. */}
          <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
            <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
              Avg cost per mode · {period}
            </h3>
            {costByMode.isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
            {!costByMode.isLoading && (costByMode.data?.modes.length ?? 0) === 0 && (
              <p className="text-gray-500 text-sm">No LLM calls yet.</p>
            )}
            <div className="space-y-2">
              {(costByMode.data?.modes ?? []).map(m => {
                const label = RESPONSE_TYPE_LABELS[m.response_type]
                return (
                  <div key={m.response_type} className="border-b border-[#2e2e2e] pb-2 last:border-0">
                    <div className="flex items-center justify-between">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${label?.color ?? 'bg-gray-500/15 text-gray-400'}`}>
                        {label?.label ?? m.response_type}
                      </span>
                      <span className="text-white text-sm font-bold tabular-nums">
                        {formatCost(m.avg_cost_micros)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500 tabular-nums">
                      <span>
                        {m.llm_calls.toLocaleString()} LLM
                        {m.cache_hits > 0 && (
                          <span className="text-emerald-400"> · {m.cache_hits.toLocaleString()} cached</span>
                        )}
                      </span>
                      <span>{Math.round(m.avg_latency_ms)}ms avg · {formatCost(m.total_cost_micros)} total</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Top queries side panel */}
          <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
            <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">Top queries · {period}</h3>
            {topQueries.isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
            {!topQueries.isLoading && (topQueries.data?.queries.length ?? 0) === 0 && (
              <p className="text-gray-500 text-sm">No data yet.</p>
            )}
            <div className="space-y-2">
              {(topQueries.data?.queries ?? []).map((q, i) => (
                <div key={i} className="border-b border-[#2e2e2e] pb-2 last:border-0">
                  <p className="text-white text-sm truncate" title={q.normalized}>{q.normalized}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                    <span>×{q.times_asked}</span>
                    {q.cache_hits > 0 && (
                      <span className="text-emerald-400">{Math.round(q.cache_hits / q.times_asked * 100)}% cached</span>
                    )}
                    {q.converted > 0 && (
                      <span className="text-emerald-400">{q.converted} → cart</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Kpi({ icon, label, value, sublabel }: { icon: React.ReactNode; label: string; value: string; sublabel?: string }) {
  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-xs">{label}</span>
        <span className="text-[#C41E3A]">{icon}</span>
      </div>
      <p className="text-xl font-bold text-white tabular-nums">{value}</p>
      {sublabel && <p className="text-[10px] text-gray-500 mt-1">{sublabel}</p>}
    </div>
  )
}
